import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, X, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Global Cmd+K command palette. Mounts once at App root.
 * Searches RFQs, projects, tasks, suppliers, users, invoices, intel notes.
 */
export default function CommandPalette() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const debounceRef = useRef();
  const inputRef = useRef();

  // Cmd+K / Ctrl+K toggle (only for authenticated users)
  useEffect(() => {
    if (!user) return;
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [user]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQ("");
      setGroups([]);
      setActive(0);
    }
  }, [open]);

  // Debounced fetch
  useEffect(() => {
    if (!open) return;
    if (!q.trim()) {
      setGroups([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/search`, {
          params: { q: q.trim() },
          withCredentials: true,
        });
        setGroups(r.data?.groups || []);
        setActive(0);
      } catch {
        setGroups([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [q, open]);

  // Flatten for keyboard navigation
  const flat = groups.flatMap((g) =>
    g.items.map((i) => ({ ...i, _group: g.label }))
  );

  const onKey = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((a) => Math.min(a + 1, flat.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((a) => Math.max(a - 1, 0));
      } else if (e.key === "Enter" && flat[active]) {
        e.preventDefault();
        navigate(flat[active].url);
        setOpen(false);
      }
    },
    [flat, active, navigate]
  );

  if (!user || !open) return null;

  return (
    <div
      data-testid="command-palette"
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4 bg-black/50 backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-2xl bg-white border border-[#E2E8F0] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[#E2E8F0]">
          <Search size={18} className="text-[#1C3E5F]" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search RFQs, projects, tasks, suppliers, invoices…"
            className="flex-1 outline-none border-0 text-[15px] placeholder:text-[#94A3B8]"
            data-testid="command-palette-input"
          />
          <kbd className="text-[10px] px-2 py-1 bg-[#F1F5F9] text-[#64748B] border border-[#E2E8F0]">
            ESC
          </kbd>
          <button
            onClick={() => setOpen(false)}
            className="p-1 hover:bg-[#F1F5F9]"
            aria-label="Close"
          >
            <X size={16} className="text-[#64748B]" />
          </button>
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {loading && (
            <div className="px-5 py-10 text-center text-sm text-[#64748B]">
              Searching…
            </div>
          )}
          {!loading && q && flat.length === 0 && (
            <div className="px-5 py-10 text-center text-sm text-[#64748B]">
              No results for &quot;{q}&quot;
            </div>
          )}
          {!loading && !q && (
            <div className="px-5 py-8 text-center text-xs text-[#94A3B8] uppercase tracking-[0.18em]">
              Type to search across the platform
            </div>
          )}
          {!loading && groups.map((g, gi) => {
            const offset = groups.slice(0, gi).reduce((a, x) => a + x.items.length, 0);
            return (
              <div key={g.label}>
                <div className="px-5 py-2 text-[10px] uppercase tracking-[0.18em] text-[#94A3B8] bg-[#F8FAFC] border-y border-[#E2E8F0]">
                  {g.label}
                </div>
                {g.items.map((it, idx) => {
                  const flatIdx = offset + idx;
                  return (
                    <button
                      key={it.id}
                      onClick={() => {
                        navigate(it.url);
                        setOpen(false);
                      }}
                      onMouseEnter={() => setActive(flatIdx)}
                      data-testid={`cmd-result-${it.id}`}
                      className={`w-full text-left px-5 py-3 flex items-center justify-between transition-colors ${
                        flatIdx === active ? "bg-[#F8FAFC]" : "bg-white hover:bg-[#F8FAFC]"
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-[#12283F] font-medium truncate">
                          {it.title}
                        </div>
                        {it.subtitle && (
                          <div className="text-xs text-[#64748B] truncate mt-0.5">
                            {it.subtitle}
                          </div>
                        )}
                      </div>
                      <ArrowRight size={14} className="text-[#94A3B8] shrink-0 ml-3" />
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="px-5 py-2.5 border-t border-[#E2E8F0] flex items-center justify-between text-[10px] uppercase tracking-[0.16em] text-[#94A3B8] bg-[#F8FAFC]">
          <div className="flex items-center gap-3">
            <span>↑↓ navigate</span>
            <span>↵ open</span>
          </div>
          <div>
            <kbd className="px-1.5 py-0.5 bg-white border border-[#E2E8F0]">⌘K</kbd>
            <span className="ml-2">Fortune Search</span>
          </div>
        </div>
      </div>
    </div>
  );
}
